const STORAGE_KEY = "browser-features.json";
const FEATURE_VERSION = 1;
export const BROWSER_FEATURES_CHANGED_EVENT = "sumatra-online:browser-features-changed";

const IMAGE_TESTS: Array<{ ext: string; aliases?: string[] }> = [
  { ext: "png" },
  { ext: "jpg", aliases: ["jpeg", "jfif"] },
  { ext: "gif" },
  { ext: "bmp" },
  { ext: "webp" },
  { ext: "avif" },
  { ext: "ico" },
  { ext: "svg" },
];

const FALLBACK_TEST_IMAGES: Record<string, { mime: string; base64: string }> = {
  avif: {
    mime: "image/avif",
    base64: "AAAAHGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZgAAAOptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAAAAAAA5waXRtAAAAAAABAAAAImlsb2MAAAAAREAAAQABAAAAAAEOAAEAAAAAAAAAEgAAACNpaW5mAAAAAAABAAAAFWluZmUCAAAAAAEAAGF2MDEAAAAAamlwcnAAAABLaXBjbwAAABNjb2xybmNseAABAA0ABoAAAAAMYXYxQ4EgAgAAAAAUaXNwZQAAAAAAAAABAAAAAQAAABBwaXhpAAAAAAMICAgAAAAXaXBtYQAAAAAAAAABAAEEAYIDBAAAABptZGF0EgAKBzgABhAQ0GkyBRAAAAtA",
  },
  bmp: {
    mime: "image/bmp",
    base64: "Qk06AAAAAAAAADYAAAAoAAAAAQAAAAEAAAABABgAAAAAAAQAAAATCwAAEwsAAAAAAAAAAAAA////AA==",
  },
  gif: {
    mime: "image/gif",
    base64: "R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
  },
  ico: {
    mime: "image/x-icon",
    base64: "AAABAAEAAQEAAAEAIAAwAAAAFgAAACgAAAABAAAAAgAAAAEAIAAAAAAABAAAABMLAAATCwAAAAAAAAAAAAD/////AAAAAA==",
  },
  jpg: {
    mime: "image/jpeg",
    base64: "/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAP//////////////////////////////////////////////////////////////////////////////////////wgALCAABAAEBAREA/8QAFBABAAAAAAAAAAAAAAAAAAAAAP/aAAgBAQABPxA=",
  },
  png: {
    mime: "image/png",
    base64: "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=",
  },
  svg: {
    mime: "image/svg+xml",
    base64: "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiLz4=",
  },
  webp: {
    mime: "image/webp",
    base64: "UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA",
  },
};

export interface BrowserFeatures {
  version: number;
  checkedAt: string;
  imageFormats: Record<string, boolean>;
}

export function getCachedBrowserFeatures(): BrowserFeatures | null {
  try {
    const text = localStorage.getItem(STORAGE_KEY);
    const features = text ? JSON.parse(text) as BrowserFeatures : null;
    return features?.version === FEATURE_VERSION ? features : null;
  } catch {
    return null;
  }
}

export async function checkBrowserFeatures(): Promise<BrowserFeatures> {
  const imageFormats: Record<string, boolean> = {};
  for (const test of IMAGE_TESTS) {
    const supported = await canDecodeTestImage(test.ext);
    imageFormats[test.ext] = supported;
    for (const alias of test.aliases ?? []) {
      imageFormats[alias] = supported;
    }
  }

  const features: BrowserFeatures = {
    version: FEATURE_VERSION,
    checkedAt: new Date().toISOString(),
    imageFormats,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
  window.dispatchEvent(new CustomEvent(BROWSER_FEATURES_CHANGED_EVENT, { detail: features }));
  return features;
}

async function canDecodeTestImage(ext: string): Promise<boolean> {
  let objectURL = "";
  try {
    const blob = testImageBlob(ext);
    if (!blob) {
      return false;
    }
    objectURL = URL.createObjectURL(blob);
    const image = await loadImage(objectURL);
    if ((image.naturalWidth || image.width) <= 0 || (image.naturalHeight || image.height) <= 0) {
      return false;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return false;
    }
    ctx.drawImage(image, 0, 0, 1, 1);
    return true;
  } catch {
    return false;
  } finally {
    if (objectURL) {
      URL.revokeObjectURL(objectURL);
    }
  }
}

function testImageBlob(ext: string): Blob | null {
  const image = FALLBACK_TEST_IMAGES[ext];
  if (!image) {
    return null;
  }
  const binary = atob(image.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: image.mime });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("image decode failed"));
    image.src = src;
  });
}
